import React, { FC, memo } from 'react';
import { Box, Button, GreenButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n, useLicenses, useSelector } from '~Hooks';
import CallIcon from '~Assets/icons/call.svg';
import MailIcon from '~Assets/icons/mail.svg';
import PercentDiscountIcon from '~Assets/icons/percent_discount.svg';
import LoyaltyIcon from '~Assets/icons/loyalty.svg';
import Icon from '~ComponentsImproved/Icon';
import { useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';
import OrganizationInfo from './OrganizationInfo';
import LoadingSkeleton from './LoadingSkeleton';
import { GET_USER } from '../api';
import { GetUser, GetUserVariables } from '../__queries__';
import MembershipLevelBadge from './MembershipLevelBadge';
import UserStampCards from './UserStampCards';

export const CancelButton = styled(Button)`
  max-width: 160px;
  white-space: nowrap;
`;

interface Props {
  userId: string;
  onCancel?: () => void;
  onConfirm?: (memberDiscountRate?: number) => void;
}

/** UI Component for the selected user - CTA to confirm & close modal */
const SelectedUser: FC<Props> = ({ userId, onCancel, onConfirm }) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const country = useSelector((state) => state.country);
  const isHandy = window.innerWidth <= 600;

  // Fetch user data based on userId
  const userQuery = useQuery<GetUser, GetUserVariables>(GET_USER, {
    variables: { id: userId },
    skip: !userId,
  });

  const user = userQuery.data?.User;

  // Fetch membership level from different places if the company uses membershipCards or not.
  const userMembership = user?.membershipCard?.membershipLevel ?? user?.membershipLevel;

  const cancelButton = useButton({
    onClick: () => {
      if (onCancel) {
        onCancel();
      }
    },
  });

  const confirmButton = useButton({
    onClick: () => {
      if (onConfirm) {
        onConfirm(userMembership?.discountPercentage);
      }
    },
  });

  if (userQuery.loading) {
    return (
      <LoadingSkeleton>
        <Box display="flex" flexDirection="row" gap="xs" marginTop="xs" alignItems="center" fluid>
          {onCancel && (
            <CancelButton fluid={!isHandy} {...cancelButton}>
              {i18n('Order.Remove')}
            </CancelButton>
          )}

          {onConfirm && (
            <GreenButton fluid {...confirmButton}>
              {isHandy
                ? i18n('CustomerForCheckout.ConnectAndClose.Short')
                : i18n('CustomerForCheckout.ConnectAndClose')}
            </GreenButton>
          )}
        </Box>
      </LoadingSkeleton>
    );
  }

  // Show error or empty state if no user data
  if (!userQuery.data?.User) {
    return (
      <Paragraph level={5} color="var(--colorBlueGray200)">
        {i18n('CustomerForCheckout.NotFound')}
      </Paragraph>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="small"
      fluid
      justifyContent="center"
      alignItems="center">
      <Box display="flex" flexDirection="column" fluid>
        <Box fluid display="flex" gap="xs" alignItems="flex-start" justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <Heading level={isHandy ? 3 : 2} bottom={1} color="var(--colorWhite)">
              {user?.name}
            </Heading>
            {user?.createdAt && (
              <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
                {isHandy
                  ? i18n('CustomerForCheckout.CustomerSince.Short')
                  : i18n('CustomerForCheckout.CustomerSince')}
                : {DateTime.fromISO(user.createdAt).toFormat('d MMMM yyyy')}
              </Paragraph>
            )}
          </Box>

          {licenses.hasApp && (
            <Box display="flex" gap="xs" alignItems="flex-end" flexDirection="column">
              <MembershipLevelBadge
                title={i18n(userMembership?.title)}
                primaryColor={userMembership?.primaryColor || 'var(--colorBlueGray200)'}
              />
              <Box display="flex" gap="small" alignItems="center" style={{ bottom: '1px' }}>
                {userMembership?.discountPercentage != null && (
                  <Box display="flex" alignItems="center" gap="xs">
                    <Icon
                      src={PercentDiscountIcon}
                      height="16px"
                      width="16px"
                      color="var(--colorBlueGray300)"
                    />
                    <Paragraph level={5} bottom={0} color="var(--colorBlueGray200)">
                      {userMembership?.discountPercentage}% {i18n('POS.Discount').toLowerCase()}
                    </Paragraph>
                  </Box>
                )}
                <Box display="flex" alignItems="center" gap="xs">
                  <Icon
                    src={LoyaltyIcon}
                    height="16px"
                    width="16px"
                    color="var(--colorBlueGray300)"
                  />
                  <Paragraph level={5} bottom={0} color="var(--colorBlueGray200)">
                    {user?.membershipCard?.points || 0} {i18n('CustomerForCheckout.Points')}
                  </Paragraph>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {country === 'ES' && user?.orgNumber ? (
          <OrganizationInfo user={user} />
        ) : (
          <Box
            display="flex"
            flexDirection={isHandy ? 'column' : 'row'}
            alignItems="center"
            marginTop="xs">
            {user?.email && (
              <Box display="flex" gap="xs" alignItems="center" fluid>
                <Icon src={MailIcon} color="var(--colorBlueGray300)" />
                <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
                  {user?.email}
                </Paragraph>
              </Box>
            )}

            {user?.phone && (
              <Box display="flex" gap="xs" alignItems="center" fluid>
                <Icon src={CallIcon} color="var(--colorBlueGray300)" />
                <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
                  {user?.phone.includes('46')
                    ? user?.phone.split('46')[1]
                      ? '46' + user?.phone.split('46')[1]
                      : user?.phone
                    : user?.phone}
                </Paragraph>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {user?.stampCards && user.stampCards.length > 0 && (
        <UserStampCards stampCards={user.stampCards} stamps={user.stamps || []} />
      )}

      <Box display="flex" flexDirection="row" gap="xs" marginTop="xs" alignItems="center" fluid>
        {onCancel && (
          <CancelButton fluid={!isHandy} {...cancelButton}>
            {i18n('Order.Remove')}
          </CancelButton>
        )}

        {onConfirm && (
          <GreenButton fluid {...confirmButton}>
            {isHandy
              ? i18n('CustomerForCheckout.ConnectAndClose.Short')
              : i18n('CustomerForCheckout.ConnectAndClose')}
          </GreenButton>
        )}
      </Box>
    </Box>
  );
};

export default memo(SelectedUser);
